/* ═══════════════════════════════════════════════════════════════════
   FEMME — bmm-pdp.js
   Product Detail Page JavaScript — Heritage v3.4.0
   Version: 2.0 (Phase 2 — Commerce Zone)
   Last Updated: April 15, 2026

   MODULES:
   1. ATCShake        — shake + ripple + state machine on Add to Cart
   2. StickyATC       — Heritage sticky bar observer
   3. SocialProof     — animated counter display
   4. WishlistToggle  — heart fill toggle
   5. ATCIdlePulse    — subtle idle attention shake
   6. OffersSlider    — auto-advance + arrow nav + dots + touch swipe
   7. OffersTimer     — live countdown for flash sale slides
   8. CouponCopy      — clipboard copy + COPIED feedback
   9. GiftWrap        — toggle selection + message textarea + form injection
═══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── UTILITY ──────────────────────────────────────────────────── */
  var prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  function $(selector, context) {
    return (context || document).querySelector(selector);
  }

  function $$(selector, context) {
    return Array.prototype.slice.call(
      (context || document).querySelectorAll(selector)
    );
  }


  /* ═══════════════════════════════════════════════════════════════
     MODULE 1: ATC SHAKE + RIPPLE + STATE MACHINE
  ═══════════════════════════════════════════════════════════════ */
  function initATCShake() {
    var atcSelectors = [
      '.product-information button[name="add"]',
      '.product-information .add-to-cart-button',
      '[data-testid="standalone-add-to-cart"]'
    ];

    var atcBtn = null;
    for (var i = 0; i < atcSelectors.length; i++) {
      atcBtn = $(atcSelectors[i]);
      if (atcBtn) break;
    }
    if (!atcBtn) return;

    var stickyAtcBtn = $('.sticky-add-to-cart__button');

    function applyShake(btn, event) {
      if (prefersReducedMotion) return;
      btn.classList.remove('bmm-pdp-shake');
      void btn.offsetWidth;
      btn.classList.add('bmm-pdp-shake');

      if (event) {
        var rect = btn.getBoundingClientRect();
        var ripple = document.createElement('span');
        ripple.className = 'bmm-pdp-ripple';
        var size = Math.max(btn.offsetWidth, btn.offsetHeight) * 2;
        var x = (event.clientX || rect.left + rect.width / 2) - rect.left - size / 2;
        var y = (event.clientY || rect.top + rect.height / 2) - rect.top - size / 2;
        ripple.style.width = size + 'px';
        ripple.style.height = size + 'px';
        ripple.style.left = x + 'px';
        ripple.style.top = y + 'px';
        btn.appendChild(ripple);
        setTimeout(function () {
          if (ripple.parentNode) ripple.parentNode.removeChild(ripple);
        }, 600);
      }

      btn.addEventListener('animationend', function onAnimEnd() {
        btn.classList.remove('bmm-pdp-shake');
        btn.removeEventListener('animationend', onAnimEnd);
      });
    }

    /* Heritage handles all ATC text states (Adding.../Added to Cart) natively.
       We only add our shake + ripple effect on click. */
    function onATCClick(event) {
      applyShake(atcBtn, event);
    }

    atcBtn.addEventListener('click', onATCClick, false);

    if (stickyAtcBtn) {
      stickyAtcBtn.addEventListener('click', function (event) {
        applyShake(stickyAtcBtn, event);
      }, false);
    }
  }


  /* ═══════════════════════════════════════════════════════════════
     MODULE 2: STICKY ATC OBSERVER
  ═══════════════════════════════════════════════════════════════ */
  function initStickyATCObserver() {
    var stickyBar = $('.sticky-add-to-cart__bar');
    if (!stickyBar) return;

    var observer = new MutationObserver(function () {
      /* Heritage manages sticky — we just observe */
    });

    observer.observe(stickyBar, {
      attributes: true,
      attributeFilter: ['data-stuck', 'class']
    });
  }


  /* ═══════════════════════════════════════════════════════════════
     MODULE 3: SOCIAL PROOF STRIP
  ═══════════════════════════════════════════════════════════════ */
  function initSocialProof() {
    var items = $$('.bmm-pdp-social-proof__count');
    if (!items.length) return;

    if (prefersReducedMotion) {
      items.forEach(function (el) {
        el.textContent = el.dataset.bmmTarget || el.textContent;
      });
      return;
    }

    items.forEach(function (el) {
      var target = parseInt(el.dataset.bmmTarget || el.textContent, 10);
      if (isNaN(target) || target <= 0) return;

      var duration = 1200;
      var startTime = null;

      function step(timestamp) {
        if (!startTime) startTime = timestamp;
        var progress = Math.min((timestamp - startTime) / duration, 1);
        var eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = Math.round(eased * target).toLocaleString();
        if (progress < 1) requestAnimationFrame(step);
      }

      setTimeout(function () { requestAnimationFrame(step); }, 300);
    });
  }


  /* ═══════════════════════════════════════════════════════════════
     MODULE 4: WISHLIST TOGGLE
  ═══════════════════════════════════════════════════════════════ */
  function initWishlistToggle() {
    $$('.bmm-pdp-wishlist-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        btn.classList.toggle('bmm-pdp-wishlisted');
        var isWishlisted = btn.classList.contains('bmm-pdp-wishlisted');
        btn.setAttribute('aria-label', isWishlisted ? 'Remove from wishlist' : 'Add to wishlist');
        btn.setAttribute('aria-pressed', isWishlisted ? 'true' : 'false');
      });
    });
  }


  /* ═══════════════════════════════════════════════════════════════
     MODULE 5: ATC IDLE ATTENTION PULSE
     Fires subtle shake every 8s to draw attention.
     Stops on click. Pauses on tab switch.
  ═══════════════════════════════════════════════════════════════ */
  function initATCIdlePulse() {
    if (prefersReducedMotion) return;

    var atcSelectors = [
      '.product-information button[name="add"]',
      '.product-information .add-to-cart-button',
      '[data-testid="standalone-add-to-cart"]'
    ];

    var atcBtn = null;
    for (var i = 0; i < atcSelectors.length; i++) {
      atcBtn = $(atcSelectors[i]);
      if (atcBtn) break;
    }
    if (!atcBtn) return;

    var stopped = false;
    var intervalId = null;

    function fireIdleShake() {
      if (stopped || document.hidden) return;
      if (atcBtn.classList.contains('bmm-pdp-shake')) return;

      atcBtn.classList.remove('bmm-pdp-idle-shake');
      void atcBtn.offsetWidth;
      atcBtn.classList.add('bmm-pdp-idle-shake');

      atcBtn.addEventListener('animationend', function onEnd() {
        atcBtn.classList.remove('bmm-pdp-idle-shake');
        atcBtn.removeEventListener('animationend', onEnd);
      }, { once: true });
    }

    atcBtn.addEventListener('click', function () {
      stopped = true;
      clearInterval(intervalId);
    }, { once: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        clearInterval(intervalId);
      } else if (!stopped) {
        intervalId = setInterval(fireIdleShake, 8000);
      }
    });

    setTimeout(function () {
      if (!stopped) {
        fireIdleShake();
        intervalId = setInterval(fireIdleShake, 8000);
      }
    }, 3000);
  }


  /* ═══════════════════════════════════════════════════════════════
     MODULE 6: OFFERS SLIDER
     Auto-advance + arrow nav + dot nav + touch swipe.
     Reads data-auto-advance from section wrapper.
  ═══════════════════════════════════════════════════════════════ */
  function initOffersSlider() {
    var sliders = $$('.bmm-pdp-offers');
    if (!sliders.length) return;

    sliders.forEach(function (slider) {
      var track     = slider.querySelector('[data-offers-track]');
      var counter   = slider.querySelector('[data-offers-counter]');
      var prevBtn   = slider.querySelector('[data-offers-prev]');
      var nextBtn   = slider.querySelector('[data-offers-next]');
      var dots      = $$('[data-offers-dot]', slider);
      var total     = parseInt(slider.dataset.total, 10) || 1;
      var advanceSec = parseInt(slider.dataset.autoAdvance, 10) || 4;

      if (!track || total <= 1) return;

      var current  = 0;
      var autoId   = null;
      var touchStartX = 0;
      var touchStartY = 0;

      function goTo(index) {
        /* Wrap around */
        current = (index + total) % total;
        track.style.transform = 'translateX(-' + (current * (100 / total)) + '%)';

        /* Counter */
        if (counter) counter.textContent = (current + 1) + ' / ' + total;

        /* Dots */
        dots.forEach(function (dot, i) {
          var active = i === current;
          dot.classList.toggle('bmm-pdp-offers__dot--active', active);
          dot.setAttribute('aria-selected', active ? 'true' : 'false');
        });
      }

      function startAuto() {
        if (prefersReducedMotion) return;
        clearInterval(autoId);
        autoId = setInterval(function () {
          goTo(current + 1);
        }, advanceSec * 1000);
      }

      function resetAuto() {
        clearInterval(autoId);
        startAuto();
      }

      /* Arrow buttons */
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          goTo(current - 1);
          resetAuto();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          goTo(current + 1);
          resetAuto();
        });
      }

      /* Dot buttons */
      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var idx = parseInt(dot.dataset.offersDot, 10);
          goTo(idx);
          resetAuto();
        });
      });

      /* Touch swipe */
      track.addEventListener('touchstart', function (e) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });

      track.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - touchStartX;
        var dy = e.changedTouches[0].clientY - touchStartY;
        /* Only horizontal swipe (not scroll) */
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
          goTo(dx < 0 ? current + 1 : current - 1);
          resetAuto();
        }
      }, { passive: true });

      /* Pause on hover */
      slider.addEventListener('mouseenter', function () { clearInterval(autoId); });
      slider.addEventListener('mouseleave', function () { startAuto(); });

      /* Pause when page hidden */
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          clearInterval(autoId);
        } else {
          startAuto();
        }
      });

      /* Init */
      goTo(0);
      startAuto();
    });
  }


  /* ═══════════════════════════════════════════════════════════════
     MODULE 7: OFFERS TIMER (flash sale countdown)
     Reads hours + minutes from data attributes.
     Counts down live. Stops at 00:00:00.
  ═══════════════════════════════════════════════════════════════ */
  function initOffersTimer() {
    $$('[data-offers-timer]').forEach(function (el) {
      var hours   = parseInt(el.dataset.hours, 10)   || 0;
      var minutes = parseInt(el.dataset.minutes, 10) || 0;
      var totalSeconds = (hours * 3600) + (minutes * 60);

      if (totalSeconds <= 0) return;

      function pad(n) { return String(n).padStart(2, '0'); }

      function tick() {
        if (totalSeconds <= 0) {
          el.textContent = '00:00:00';
          return;
        }
        totalSeconds--;
        var h = Math.floor(totalSeconds / 3600);
        var m = Math.floor((totalSeconds % 3600) / 60);
        var s = totalSeconds % 60;
        el.textContent = pad(h) + ':' + pad(m) + ':' + pad(s);
        setTimeout(tick, 1000);
      }

      tick();
    });
  }


  /* ═══════════════════════════════════════════════════════════════
     MODULE 8: COUPON CODE COPY
     Targets [data-offers-copy] buttons inside offers slider.
     Uses Clipboard API with graceful fallback.
  ═══════════════════════════════════════════════════════════════ */
  function initCouponCopy() {
    $$('[data-offers-copy]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation(); /* Prevent slider nav conflicts */

        var code  = btn.dataset.offersCopy;
        var label = btn.querySelector('[data-offers-copy-label]');
        if (!code || !label) return;

        var origText = label.textContent;

        function showCopied() {
          label.textContent = 'COPIED!';
          label.classList.add('copied');
          setTimeout(function () {
            label.textContent = origText;
            label.classList.remove('copied');
          }, 2000);
        }

        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(code).then(showCopied).catch(showCopied);
        } else {
          /* Fallback for older browsers */
          var ta = document.createElement('textarea');
          ta.value = code;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); } catch (err) {}
          document.body.removeChild(ta);
          showCopied();
        }
      });
    });
  }


  /* ═══════════════════════════════════════════════════════════════
     MODULE 9: GIFT WRAP
     Toggle selected state, open message textarea,
     add/remove Gift Wrapping product (Variant ID: 63449744638321)
     from cart via Ajax Cart API. Price: INR 100.
  ═══════════════════════════════════════════════════════════════ */
  function initGiftWrap() {
    /*
      GIFT WRAP — BULLETPROOF IMPLEMENTATION
      =======================================
      Issues solved:
      1. Race condition (ATC/BIN click before fetch resolves)
         → Disable ATC + BIN buttons during fetch, re-enable on resolve
      2. Buy It Now bypasses cart
         → BIN uses same product form as ATC, so same fix applies
      3. Duplicate gift wrap on page reload
         → Check /cart.js on init, sync UI to cart state
      4. Message update gap
         → Update line item properties directly, no remove+re-add
      5. Network failure
         → Revert UI on error, always re-enable buttons
    */
    var GIFT_WRAP_VARIANT_ID = 63449744638321;
    var wrappers = $$('.bmm-pdp-giftwrap');
    if (!wrappers.length) return;

    wrappers.forEach(function (wrapper) {
      var toggle   = wrapper.querySelector('[data-giftwrap-toggle]');
      var message  = wrapper.querySelector('[data-giftwrap-message]');
      var textarea = wrapper.querySelector('[data-giftwrap-textarea]');
      var selected = false;
      var processing = false;

      /* ── Get all buy buttons (ATC + Buy It Now) ── */
      function getBuyButtons() {
        return $$([
          '.product-information button[name="add"]',
          '.product-information .add-to-cart-button',
          '.product-information .shopify-payment-button__button',
          '.sticky-add-to-cart__button'
        ].join(','));
      }

      /* ── Disable buy buttons while gift wrap fetch is in flight ──
         Use .disabled = true — Heritage's handleClick checks this natively.
         CSS pointer-events alone is NOT enough as Heritage bypasses it.
      */
      function lockButtons() {
        getBuyButtons().forEach(function(btn) {
          btn._bmmWasDisabled = btn.disabled;
          btn.disabled = true;
          btn.style.opacity = '0.6';
        });
      }

      /* ── Re-enable buy buttons after fetch resolves ── */
      function unlockButtons() {
        getBuyButtons().forEach(function(btn) {
          btn.disabled = btn._bmmWasDisabled || false;
          delete btn._bmmWasDisabled;
          btn.style.opacity = '';
        });
      }

      /* ── Add gift wrap to cart ──
         Includes Heritage's sections parameter so cart drawer
         re-renders automatically with the new item + updated total.
      */
      function addGiftWrap(callback) {
        processing = true;
        lockButtons();

        /* Collect cart section IDs Heritage uses for re-rendering */
        var cartSectionIds = [];
        document.querySelectorAll('[data-section-id]').forEach(function(el) {
          var sid = el.dataset.sectionId;
          if (sid && (
            el.tagName.toLowerCase() === 'cart-items-component' ||
            el.classList.contains('cart-drawer') ||
            sid.indexOf('cart') !== -1
          )) {
            if (cartSectionIds.indexOf(sid) === -1) cartSectionIds.push(sid);
          }
        });

        var body = {
          items: [{ id: GIFT_WRAP_VARIANT_ID, quantity: 1 }]
        };
        if (textarea && textarea.value.trim()) {
          body.items[0].properties = { 'Gift Message': textarea.value.trim() };
        }
        if (cartSectionIds.length) {
          body.sections = cartSectionIds.join(',');
          body.sections_url = window.location.pathname;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        })
        .then(function(r) { return r.json(); })
        .then(function(data) {
          processing = false;
          unlockButtons();

          /* Dispatch CartAddEvent with sections so Heritage re-renders drawer */
          var CartAddEventName = 'cart:add';
          /* Try to use Heritage's own CartAddEvent if available */
          try {
            var evt = new CustomEvent('cart:add', {
              bubbles: true,
              detail: { data: { sections: data.sections || {} } }
            });
            document.dispatchEvent(evt);
          } catch(e) {}

          /* Also trigger cart-icon refresh */
          document.dispatchEvent(new CustomEvent('cart:refresh'));

          /* Force cart drawer to re-render if it's open */
          var cartItems = document.querySelector('cart-items-component');
          if (cartItems && typeof cartItems.renderSection === 'function') {
            cartItems.renderSection(cartItems.sectionId, { cache: false });
          }

          if (callback) callback();
        })
        .catch(function(err) {
          console.warn('[FEMME] Gift wrap add failed:', err);
          processing = false;
          unlockButtons();
          selected = false;
          updateUI();
        });
      }

      /* ── Remove gift wrap from cart ── */
      function removeGiftWrap(callback) {
        processing = true;
        lockButtons();

        var updates = {};
        updates[String(GIFT_WRAP_VARIANT_ID)] = 0;

        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: updates })
        })
        .then(function() {
          processing = false;
          unlockButtons();
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          if (callback) callback();
        })
        .catch(function(err) {
          console.warn('[FEMME] Gift wrap remove failed:', err);
          processing = false;
          unlockButtons();
        });
      }

      /* ── Update gift message without removing item ── */
      function updateGiftMessage() {
        if (!selected || !textarea) return;
        clearTimeout(textarea._debounce);
        textarea._debounce = setTimeout(function() {
          /* Use /cart/change.js with line_id to update properties only */
          /* First get cart to find line item key */
          fetch('/cart.js')
          .then(function(r) { return r.json(); })
          .then(function(cart) {
            var lineItem = null;
            cart.items.forEach(function(item) {
              if (item.variant_id === GIFT_WRAP_VARIANT_ID) {
                lineItem = item;
              }
            });
            if (!lineItem) return;

            var properties = { 'Gift Message': textarea.value.trim() };
            fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: lineItem.key,
                quantity: 1,
                properties: properties
              })
            });
          });
        }, 800);
      }

      /* ── Sync UI state with actual cart on page load ── */
      function syncWithCart() {
        fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var inCart = false;
          cart.items.forEach(function(item) {
            if (item.variant_id === GIFT_WRAP_VARIANT_ID) {
              inCart = true;
              /* Restore gift message in textarea if present */
              if (textarea && item.properties && item.properties['Gift Message']) {
                textarea.value = item.properties['Gift Message'];
              }
            }
          });
          if (inCart && !selected) {
            selected = true;
            updateUI();
          } else if (!inCart && selected) {
            selected = false;
            updateUI();
          }
        })
        .catch(function() {
          /* Silently fail — don't block anything */
        });
      }

      /* ── UI state update ── */
      function updateUI() {
        wrapper.classList.toggle('bmm-pdp-giftwrap--selected', selected);
        if (toggle) toggle.setAttribute('aria-checked', selected ? 'true' : 'false');
        if (message) {
          message.classList.toggle('bmm-pdp-giftwrap__message--open', selected);
        }
      }

      /* ── Toggle on click ── */
      wrapper.addEventListener('click', function (e) {
        if (textarea && textarea.contains(e.target)) return;
        if (processing) return;
        selected = !selected;
        updateUI();
        if (selected) {
          addGiftWrap();
        } else {
          removeGiftWrap();
        }
      });

      /* Keyboard accessibility */
      if (toggle) {
        toggle.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (processing) return;
            selected = !selected;
            updateUI();
            if (selected) { addGiftWrap(); } else { removeGiftWrap(); }
          }
        });
      }

      /* Textarea — update message on existing cart item */
      if (textarea) {
        textarea.addEventListener('input', updateGiftMessage);
        textarea.addEventListener('click', function (e) { e.stopPropagation(); });
      }

      /* Sync with cart on init — handles page reload with gift wrap already in cart */
      syncWithCart();
    });
  }


  /* ═══════════════════════════════════════════════════════════════
     INIT
  ═══════════════════════════════════════════════════════════════ */
  function init() {
    setTimeout(function () {
      initATCShake();
      initStickyATCObserver();
      initSocialProof();
      initWishlistToggle();
      initATCIdlePulse();
      initOffersSlider();
      initOffersTimer();
      initCouponCopy();
      initGiftWrap();
    }, 80);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.detail && event.detail.sectionId &&
        event.detail.sectionId.indexOf('product') !== -1) {
      init();
    }
    /* Also re-init offers/giftwrap if those sections reload */
    if (event.detail && event.detail.sectionId) {
      var id = event.detail.sectionId;
      if (id.indexOf('offers') !== -1 || id.indexOf('giftwrap') !== -1) {
        setTimeout(function () {
          initOffersSlider();
          initOffersTimer();
          initCouponCopy();
          initGiftWrap();
        }, 80);
      }
    }
  });

})();
